import { Component, OnInit, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { PurchasingService } from '../../../services/purchasing.service';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { PrintService } from '../../../services/print.service';
import { DashboardStatBucket } from '../../../shared/dashboard-stat-card/dashboard-stat-card.component';
import { buildDashboardThemeVars } from '../../../shared/dashboard-widget-theme';

@Component({
	selector: 'app-backorders-vs-stock',
	templateUrl: './backorders-vs-stock.component.html',
	styleUrls: ['./backorders-vs-stock.component.scss'],
	animations: [
		trigger('flipState', [
			state('active', style({ transform: 'rotate3d(0,1,0,179deg)' })),
			state('inactive', style({ transform: 'rotateY(0)' })),
			transition('active => inactive', animate('500ms ease-out')),
			transition('inactive => active', animate('500ms ease-in')),
		]),
	],
})
export class BackordersVsStockComponent implements OnInit, OnDestroy {

	flip = 'inactive';
	title = 'Back Orders vs Stock';
	exporting = false;
	loading = true;

	allData: any[] = [];
	totalOrders = 0;
	fillableCount = 0;
	shortCount = 0;

	displayedColumns: string[] = [
		'ORDERNO', 'DATE', 'CUSTOMER', 'SALESPERSON',
		'ITEMCODE', 'DESCRIPTION', 'OUTSTANDING', 'QOH', 'NETTOTAL'
	];

	dataSource = new MatTableDataSource<any>([]);
	filterValue = '';

	// Filter toggles
	showAll = true;
	showFillable = false;
	showShort = false;

	@ViewChild(MatSort, { static: false }) sort: MatSort;
	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild('print_table') printtable: ElementRef;

	private sub: Subscription;

	get themeVars(): Record<string, string> {
		return buildDashboardThemeVars('cw', 'rose');
	}

	get summaryBuckets(): DashboardStatBucket[] {
		if (this.loading) {
			return [];
		}
		return [
			{ key: 'fillable', label: 'Fillable', count: this.fillableCount },
			{ key: 'short', label: 'Short', count: this.shortCount },
		];
	}

	constructor(
		private purchasingService: PurchasingService,
		private globalSearchService: GlobalSearchService,
		private printService: PrintService,
		private router: Router,
	) {}

	ngOnInit(): void {
		this.sub = this.purchasingService.getBackOrders({}).subscribe((results: any[]) => {
			this.allData = results || [];
			this.totalOrders = this.allData.length;
			this.fillableCount = this.allData.filter(r => +r.QOH >= +r.OUTSTANDING).length;
			this.shortCount = this.allData.filter(r => +r.QOH < +r.OUTSTANDING).length;
			this.applyPresetFilter();
			this.loading = false;
		});
	}

	ngAfterViewInit(): void {
		this.dataSource.sort = this.sort;
		this.dataSource.paginator = this.paginator;
		this.dataSource.filterPredicate = (row: any, filter: string) => {
			const s = filter.toLowerCase();
			return [row.ORDERNO, row.CUSTOMER, row.NAME, row.ITEMCODE, row.DESCRIPTION, row.SALESPERSON]
				.some(v => (v || '').toString().toLowerCase().includes(s));
		};
	}

	toggleFlip() {
		this.flip = this.flip === 'active' ? 'inactive' : 'active';
	}

	onSummaryBucket(bucket: DashboardStatBucket): void {
		if (bucket.key === 'fillable') {
			this.setFilter('fillable');
		} else if (bucket.key === 'short') {
			this.setFilter('short');
		}
		this.flip = 'active';
	}

	applyFilter(event: Event) {
		this.filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();
		this.dataSource.filter = this.filterValue;
	}

	setFilter(mode: 'all' | 'fillable' | 'short') {
		this.showAll = mode === 'all';
		this.showFillable = mode === 'fillable';
		this.showShort = mode === 'short';
		this.applyPresetFilter();
	}

	private applyPresetFilter() {
		let filtered = this.allData;
		if (this.showFillable) {
			filtered = this.allData.filter(r => +r.QOH >= +r.OUTSTANDING);
		} else if (this.showShort) {
			filtered = this.allData.filter(r => +r.QOH < +r.OUTSTANDING);
		}
		this.dataSource.data = filtered;
		if (this.sort) { this.dataSource.sort = this.sort; }
		if (this.paginator) { this.dataSource.paginator = this.paginator; }
		if (this.filterValue) { this.dataSource.filter = this.filterValue; }
	}

	rowClass(row: any): string {
		const qoh = +row.QOH;
		const outstanding = +row.OUTSTANDING;
		if (qoh >= outstanding) { return 'row-fillable'; }
		if (qoh > 0) { return 'row-partial'; }
		return 'row-short';
	}

	goToItem(stockid: string) {
		this.router.navigate(['/inventory/item', stockid]);
	}

	goToOrder(orderno: string) {
		this.router.navigate(['/orders/order-inquiry', orderno]);
	}

	exportPdf() {
		this.exporting = true;
		const encoded = this.globalSearchService.base64encode(this.printtable.nativeElement.innerHTML);
		const today = new Date();
		const data = {
			content: encoded,
			filename: 'backorders-vs-stock.pdf',
			landscape: true,
			title: 'Back Orders vs Stock',
			subtitle: 'Generated ' + today.toLocaleString(),
		};
		this.printService.pdf(data).subscribe((result: any) => {
			this.globalSearchService.downloadPdf(result.content, data.filename);
			this.exporting = false;
		});
	}

	exportXls() {
		this.exporting = true;
		const encoded = this.globalSearchService.base64encode(this.printtable.nativeElement.innerHTML);
		const today = new Date();
		const data = {
			content: encoded,
			filename: 'backorders-vs-stock.xlsx',
			title: 'Back Orders vs Stock',
			subtitle: 'Generated ' + today.toLocaleString(),
		};
		this.printService.xls(data).subscribe((result: any) => {
			this.globalSearchService.downloadXls(result.content, data.filename);
			this.exporting = false;
		});
	}

	ngOnDestroy() {
		if (this.sub) { this.sub.unsubscribe(); }
	}
}
